'use client';

import React, { useState } from 'react';
import { Exam } from '@/lib/services/examService';
import { Calendar, Globe, Users, Building2, BookOpen, ChevronDown, ChevronUp, CheckCircle2, Clock, AlertCircle, Info, TrendingDown, ExternalLink, FileText, Target } from 'lucide-react';
import ExamCutoffChart from './ExamCutoffChart';

interface ExamDetailPanelProps {
  exam: Exam;
}

const tabs = ['Overview', 'Important Dates', 'Exam Pattern', 'Cutoff Scores', 'Syllabus'];

export default function ExamDetailPanel({ exam }: ExamDetailPanelProps) {
  const [activeTab, setActiveTab] = useState('Overview');
  const [openSyllabus, setOpenSyllabus] = useState<string | null>(exam.syllabusSections[0]?.subject ?? null);

  const streamBadgeClass =
    exam.stream[0] === 'Engineering' ? 'stream-badge-engineering' :
    exam.stream[0] === 'Medical' ? 'stream-badge-medical' :
    exam.stream[0] === 'Management' ? 'stream-badge-management' :
    exam.stream[0] === 'Law' ? 'stream-badge-law' :
    exam.stream[0] === 'Arts'? 'stream-badge-arts' : 'stream-badge-commerce';

  const modeColor =
    exam.mode === 'Online' ? 'text-green-600 bg-green-50 border-green-200' :
    exam.mode === 'Offline'? 'text-red-600 bg-red-50 border-red-200' : 'text-amber-600 bg-amber-50 border-amber-200';

  const statusIcon = (status: string) => {
    if (status === 'completed') return <CheckCircle2 size={14} className="text-green-500 shrink-0" />;
    if (status === 'ongoing') return <AlertCircle size={14} className="text-amber-500 shrink-0" />;
    return <Clock size={14} className="text-blue-500 shrink-0" />;
  };

  const formatApplicants = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return n.toString();
  };

  return (
    <div className="card-base overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="p-5 border-b border-border bg-gradient-to-r from-secondary/5 to-transparent">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${streamBadgeClass}`}>
                {exam.stream.join(' & ')}
              </span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${modeColor}`}>
                {exam.mode}
              </span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                {exam.level}
              </span>
            </div>
            <h2 className="text-xl font-bold text-foreground mb-0.5">{exam.name}</h2>
            <p className="text-sm text-muted-foreground">{exam.conductingBody}</p>
          </div>
          <a
            href={`https://${exam.officialWebsite}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-accent transition-colors shrink-0"
          >
            <Globe size={13} />
            Official Site
            <ExternalLink size={11} />
          </a>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Exam Date', value: exam.examDate, icon: Calendar },
            { label: 'Total Applicants', value: formatApplicants(exam.totalApplicants), icon: Users },
            { label: 'Total Seats', value: exam.totalSeats.toLocaleString(), icon: Building2 },
            { label: 'Application Fee', value: `₹${exam.applicationFee.toLocaleString()}`, icon: FileText },
          ].map((stat) => (
            <div key={`stat-${stat.label}`} className="bg-muted rounded-lg p-3 flex items-center gap-2">
              <stat.icon size={16} className="text-primary shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground leading-tight">{stat.label}</p>
                <p className="text-sm font-bold text-foreground font-tabular truncate">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border overflow-x-auto scrollbar-thin">
        <div className="flex gap-0 min-w-max px-4">
          {tabs.map((tab) => (
            <button
              key={`exam-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="p-5">
        {/* Overview */}
        {activeTab === 'Overview' && (
          <div className="space-y-5 animate-fade-in">
            <div>
              <h3 className="font-bold text-foreground mb-2 flex items-center gap-2">
                <Info size={16} className="text-primary" />
                About {exam.shortName}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{exam.description}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Eligibility</h4>
                <p className="text-sm text-foreground leading-relaxed">{exam.eligibility}</p>
              </div>
              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Frequency</h4>
                <p className="text-sm text-foreground">{exam.frequency}</p>
                <div className="mt-3 pt-3 border-t border-border">
                  <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Official Website</h4>
                  <a href={`https://${exam.officialWebsite}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1">
                    {exam.officialWebsite}
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </div>

            {/* Upcoming dates snippet */}
            <div>
              <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
                <Calendar size={16} className="text-primary" />
                Upcoming Important Dates
              </h3>
              <div className="space-y-2">
                {exam.importantDates.filter((d) => d.status !== 'completed').slice(0, 3).map((date, i) => (
                  <div key={`upcoming-${exam.id}-${i}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted">
                    {statusIcon(date.status)}
                    <span className="text-sm text-foreground flex-1">{date.event}</span>
                    <span className="text-sm font-semibold text-foreground font-tabular">{date.date}</span>
                  </div>
                ))}
                {exam.importantDates.filter((d) => d.status !== 'completed').length === 0 && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700">
                    <CheckCircle2 size={16} />
                    <span className="text-sm font-semibold">All dates for this session have passed.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Important Dates */}
        {activeTab === 'Important Dates' && (
          <div className="animate-fade-in">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Event</th>
                    <th className="text-left py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {exam.importantDates.map((date, i) => (
                    <tr
                      key={`date-row-${exam.id}-${i}`}
                      className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/30'} hover:bg-muted/60 transition-colors`}
                    >
                      <td className="py-3 px-4 font-medium text-foreground">{date.event}</td>
                      <td className="py-3 px-4 font-tabular text-foreground">{date.date}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          date.status === 'completed' ? 'bg-green-50 text-green-700' :
                          date.status === 'ongoing'? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                        }`}>
                          {statusIcon(date.status)}
                          {date.status.charAt(0).toUpperCase() + date.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Exam Pattern */}
        {activeTab === 'Exam Pattern' && (
          <div className="animate-fade-in space-y-4">
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle size={16} className="text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                Exam pattern shown is for the most recent session. Check the official website for any updates.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider rounded-tl-lg">Section</th>
                    <th className="text-center py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Questions</th>
                    <th className="text-center py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Max Marks</th>
                    <th className="text-center py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Duration</th>
                    <th className="text-left py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider rounded-tr-lg">Negative Marking</th>
                  </tr>
                </thead>
                <tbody>
                  {exam.pattern.map((section, i) => (
                    <tr
                      key={`pattern-${exam.id}-${i}`}
                      className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/30'} hover:bg-muted/60 transition-colors`}
                    >
                      <td className="py-3 px-4 font-semibold text-foreground">{section.section}</td>
                      <td className="py-3 px-4 text-center font-tabular font-medium text-foreground">{section.questions}</td>
                      <td className="py-3 px-4 text-center font-tabular font-bold text-primary">{section.marks}</td>
                      <td className="py-3 px-4 text-center text-foreground">{section.duration}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{section.negativeMarking}</td>
                    </tr>
                  ))}
                  <tr className="bg-secondary/5 font-bold">
                    <td className="py-3 px-4 text-foreground">Total</td>
                    <td className="py-3 px-4 text-center font-tabular text-foreground">
                      {exam.pattern.reduce((acc, s) => acc + s.questions, 0)}
                    </td>
                    <td className="py-3 px-4 text-center font-tabular text-primary">
                      {exam.pattern.reduce((acc, s) => acc + s.marks, 0)}
                    </td>
                    <td className="py-3 px-4 text-center text-muted-foreground">—</td>
                    <td className="py-3 px-4 text-muted-foreground">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Cutoff Scores */}
        {activeTab === 'Cutoff Scores' && (
          <div className="animate-fade-in space-y-5">
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info size={16} className="text-blue-600 shrink-0" />
              <p className="text-sm text-blue-800">
                Cutoff scores shown are for 2025 session. Actual 2026 cutoffs will vary based on exam difficulty and seat availability.
              </p>
            </div>

            {exam.collegeCutoffs.length > 0 ? (
              <>
                <ExamCutoffChart cutoffs={exam.collegeCutoffs} unit={exam.collegeCutoffs[0]?.unit ?? ''} />

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="text-left py-3 px-4 font-semibold text-muted-foreground text-xs uppercase tracking-wider">College</th>
                        <th className="text-center py-3 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">NIRF</th>
                        <th className="text-center py-3 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">General</th>
                        <th className="text-center py-3 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">OBC</th>
                        <th className="text-center py-3 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">SC</th>
                        <th className="text-center py-3 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">ST</th>
                        <th className="text-center py-3 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">EWS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exam.collegeCutoffs.map((cutoff, i) => (
                        <tr
                          key={`cutoff-row-${exam.id}-${cutoff.collegeId}`}
                          className={`border-b border-border last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/30'} hover:bg-muted/60 transition-colors`}
                        >
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-semibold text-foreground">{cutoff.collegeName}</p>
                              <p className="text-xs text-muted-foreground">{cutoff.location}</p>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-center">
                            {cutoff.nirfRank ? (
                              <span className="nirf-badge">#{cutoff.nirfRank}</span>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                          {[cutoff.general, cutoff.obc, cutoff.sc, cutoff.st, cutoff.ews].map((val, ci) => (
                            <td key={`cutoff-val-${ci}`} className="py-3 px-3 text-center font-tabular font-semibold text-foreground">
                              {val}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Unit: {exam.collegeCutoffs[0]?.unit} · Year: 2025 · Source: Official counselling data
                </p>
              </>
            ) : (
              <div className="text-center py-10">
                <TrendingDown size={32} className="text-muted-foreground mx-auto mb-3" />
                <p className="font-semibold text-foreground mb-1">Cutoff data not yet available</p>
                <p className="text-sm text-muted-foreground">Cutoffs for this exam will be updated after results are declared.</p>
              </div>
            )}
          </div>
        )}

        {/* Syllabus */}
        {activeTab === 'Syllabus' && (
          <div className="animate-fade-in space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={16} className="text-primary" />
              <h3 className="font-bold text-foreground">{exam.shortName} Syllabus 2026</h3>
            </div>
            {exam.syllabusSections.map((section) => (
              <div key={`syllabus-${exam.id}-${section.subject}`} className="border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenSyllabus(openSyllabus === section.subject ? null : section.subject)}
                  className="flex items-center justify-between w-full px-4 py-3 bg-muted hover:bg-muted/80 transition-colors text-left"
                >
                  <span className="font-semibold text-foreground text-sm">{section.subject}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground font-tabular">{section.topics.length} topics</span>
                    {openSyllabus === section.subject ? (
                      <ChevronUp size={16} className="text-muted-foreground" />
                    ) : (
                      <ChevronDown size={16} className="text-muted-foreground" />
                    )}
                  </div>
                </button>
                {openSyllabus === section.subject && (
                  <div className="p-4 animate-fade-in">
                    <div className="flex flex-wrap gap-2">
                      {section.topics.map((topic) => (
                        <span
                          key={`topic-${section.subject}-${topic}`}
                          className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-muted text-foreground border border-border"
                        >
                          <Target size={10} className="text-primary" />
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}